import { Directive, ElementRef, Inject, Input, Optional, ViewContainerRef } from '@angular/core';

import {
  ComponentPortal,
  OriginConnectionPosition,
  OverlayConnectionPosition,
  OverlayRef,
  OverlayService,
  OverlayState,
} from '../core/core.module';
import { DirDirective } from '../core/rtl/rtl.module';
import { HighlightName, ITourHighlightDirective, TourHighlightPosition } from './interface';
import { TourHighlightComponent } from './tour-highlight.component';
import { TourHighlightService } from './tour-highlight.service';

@Directive({
  selector: '[rtTourHighlight]',
  exportAs: 'rtTourHighlight',
  standalone: true,
})
export class TourHighlightDirective implements ITourHighlightDirective {
  public position: TourHighlightPosition;
  public name: HighlightName;

  private tourHighlightInstance: TourHighlightComponent | null;
  private overlayRef: OverlayRef | null;

  @Input('rtTourHighlight')
  public get highlightName(): HighlightName {
    return this.name;
  }

  public set highlightName(name: HighlightName) {
    this.name = name;
  }

  @Input('rtTourHighlightPosition')
  public get tourHighlightPosition(): TourHighlightPosition {
    return this.position;
  }

  public set tourHighlightPosition(position: TourHighlightPosition) {
    this.position = position;
  }

  constructor(
    private overlay: OverlayService,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private tourHighlightService: TourHighlightService,
    @Optional() private dir: DirDirective,
    @Optional() @Inject('overlayLevel') private _level: number,
  ) {}

  public ngAfterViewInit(): void {
    this.tourHighlightService.pushActiveInstance(this);
  }

  public ngOnDestroy(): void {
    if (this.tourHighlightInstance) {
      this.disposeHighlight();
    }
  }

  public show(): void {
    if (!this.tourHighlightInstance) {
      this.createHighlight();
    }
    this.tourHighlightInstance!.show(this.position);
  }

  public hide(): void {
    if (this.tourHighlightInstance) {
      this.disposeHighlight();
    }
  }

  private createHighlight(): void {
    this.createOverlay();
    const portal = new ComponentPortal(TourHighlightComponent, this.viewContainerRef);
    this.tourHighlightInstance = this.overlayRef!.attach(portal).instance;
  }

  /** Create the overlay config and position strategy */
  private createOverlay(): void {
    const origin = this.getOrigin();
    const position = this.getOverlayPosition();
    const strategy = this.overlay
      .position()
      .connectedTo(
        this.elementRef,
        <OriginConnectionPosition>origin,
        <OverlayConnectionPosition>position,
      );
    const config = new OverlayState();
    config.positionStrategy = strategy;
    config.level = this._level;

    this.overlayRef = this.overlay.create(config);
  }

  private disposeHighlight(): void {
    this.overlayRef!.dispose();
    this.overlayRef = null;
    this.tourHighlightInstance = null;
  }

  /** Returns the origin position based on the user's position preference */
  private getOrigin(): OriginConnectionPosition | void {
    if (this.position === 'above' || this.position === 'below') {
      return { originX: 'center', originY: this.position === 'above' ? 'top' : 'bottom' };
    }

    const isDirectionLtr = !this.dir || this.dir.value === 'ltr';
    if (
      this.position === 'left' ||
      (this.position === 'before' && isDirectionLtr) ||
      (this.position === 'after' && !isDirectionLtr)
    ) {
      return { originX: 'start', originY: 'center' };
    }

    if (
      this.position === 'right' ||
      (this.position === 'after' && isDirectionLtr) ||
      (this.position === 'before' && !isDirectionLtr)
    ) {
      return { originX: 'end', originY: 'center' };
    }
  }

  /** Returns the overlay position based on the user's preference */
  private getOverlayPosition(): OverlayConnectionPosition | void {
    if (this.position === 'above') {
      return { overlayX: 'center', overlayY: 'bottom' };
    }

    if (this.position === 'below') {
      return { overlayX: 'center', overlayY: 'top' };
    }

    if (this.position === 'left' || this.position === 'before') {
      return { overlayX: 'end', overlayY: 'center' };
    }

    if (this.position === 'right' || this.position === 'after') {
      return { overlayX: 'start', overlayY: 'center' };
    }
  }
}
